import { datadogRum } from '@datadog/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { GeoApiClient, GeoLocation } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';

import { PayloadAddGeoLocations } from '@/store/types/geo-state';

import { ref } from 'vue';

const showTypeLabel = (location: GeoLocation) =>
  ['canton', 'region', 'district'].includes(location.type);

export function renderGeoLocationName(
  location: GeoLocation,
  language: HgSupportedLanguage,
): string {
  const [first, ...rest] = location.names[language];

  return [
    showTypeLabel(location) ? location.typeLabel?.[language] : undefined,
    rest.length > 0 ? `${first} (${rest.join(', ')})` : first,
  ]
    .filter((x) => !!x)
    .join(' ');
}

const filterNonExistingLocationIds = (locationIds: string[], geoLocations: GeoLocation[]) =>
  locationIds.filter(
    (locationId) => !geoLocations.find((geoLocation) => geoLocation.id === locationId),
  );

function keepNecessaryFields({
  id,
  type,
  names,
  urlNames,
  is24UrlNames,
  parentPaths,
  children,
  typeLabel,
  preposition,
  wikidataId,
  newId,
}: GeoLocation): GeoLocation {
  return {
    id,
    type,
    names,
    urlNames,
    parentPaths,
    children,
    typeLabel,
    preposition,
    wikidataId,
    is24UrlNames,
    newId,
  };
}

let geoApiClient: GeoApiClient;

const getGeoApiClient = (): GeoApiClient => {
  if (!geoApiClient) {
    geoApiClient = new GeoApiClient(import.meta.env.VUE_GEO_API_ENDPOINT);
  }
  return geoApiClient;
};

export const useGeoStore = defineStore('geo', () => {
  const geoLocations = ref<GeoLocation[]>([]);
  const unresolvableGeoIds = ref<string[]>([]);

  const getGeoLocationById = (id: string): GeoLocation | undefined =>
    geoLocations.value.find((geoLocation) => geoLocation.id === id);

  const getGeoLocationsById = (ids: string[]): GeoLocation[] =>
    ids.map((id) => getGeoLocationById(id)).filter((x): x is GeoLocation => !!x);

  const getGeoLocationName = (id: string, language: HgSupportedLanguage): string | undefined => {
    const location = getGeoLocationById(id);
    return location ? renderGeoLocationName(location, language) : undefined;
  };

  function addGeoLocations({ geoLocations: newGeoLocations }: PayloadAddGeoLocations): void {
    const uniqueGeoLocations = [...geoLocations.value, ...newGeoLocations].filter(
      (g1, i, array) => array.findIndex((g2) => g1.id === g2.id) === i,
    );
    geoLocations.value = uniqueGeoLocations.map(keepNecessaryFields);
  }

  function addUnresolvableGeoIds(ids: string[]): void {
    unresolvableGeoIds.value = [...unresolvableGeoIds.value, ...ids];
  }

  async function resolveLocationsById({
    geoLocationIds,
  }: {
    geoLocationIds: string[];
  }): Promise<void> {
    const unresolvedIds = filterNonExistingLocationIds(geoLocationIds, geoLocations.value);
    if (unresolvedIds.length === 0) {
      return;
    }

    try {
      const locationsFoundByApi: GeoLocation[] =
        await getGeoApiClient().getGeoLocationsById(unresolvedIds);

      addGeoLocations({ geoLocations: locationsFoundByApi });

      const unresolvableIds = filterNonExistingLocationIds(unresolvedIds, locationsFoundByApi);

      if (unresolvableIds.length > 0) {
        addUnresolvableGeoIds(unresolvableIds);
      }
    } catch (error: any) {
      datadogRum.addError(error, {
        extra: {
          error: JSON.stringify(error),
          statusCode: error.response?.status,
          data: JSON.stringify(error.response?.data),
          locations: unresolvedIds,
          ticketId: 'KK-80',
          functionName: 'getGeoLocationsById:SearchAPI',
        },
      });
    }
  }

  return {
    // state
    geoLocations,
    unresolvableGeoIds,
    // getters
    getGeoLocationById,
    getGeoLocationsById,
    getGeoLocationName,

    // actions
    resolveLocationsById,
  };
});
